import { CheckCircle, WarningCircle, X } from "@phosphor-icons/react";
import { AnimatePresence, motion } from "motion/react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BackstagePanel } from "./components/BackstagePanel";
import { ImportDialog, type ImportSource } from "./components/ImportDialog";
import { StageShell } from "./components/StageShell";
import {
  getEligibleEntries,
  keepLatestWinnerInPool,
  mergeEntries,
} from "./domain/project";
import { selectWeightedEntry } from "./domain/random";
import { useBackstageSidebar } from "./hooks/useBackstageSidebar";
import {
  downloadText,
  entriesToCsv,
  historyToCsv,
  projectToJson,
} from "./services/exporters";
import { parseProjectBackup } from "./services/projectImport";
import {
  playLaunchCue,
  playRevealCue,
  playStopCue,
  preloadLaunchMusic,
} from "./services/audio";
import {
  clearRememberedProject,
  loadRememberedProject,
  saveRememberedProject,
} from "./services/storage";
import type {
  DrawRecord,
  DrawRuntime,
  DrawStopReason,
  PickscapeProject,
  ThemeId,
} from "./types";
import { DEFAULT_SETTINGS } from "./types";

const REMEMBER_PREFERENCE_KEY = "pickscape:remember";

function initialProject(): PickscapeProject {
  const systemReducedMotion =
    typeof window !== "undefined" &&
    window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
  return {
    schemaVersion: 1,
    listName: "Untitled list",
    entries: [],
    history: [],
    settings: {
      ...DEFAULT_SETTINGS,
      motionReduced: systemReducedMotion,
      selectedCategories: [],
    },
    updatedAt: new Date().toISOString(),
  };
}

function initialRuntime(): DrawRuntime {
  return {
    status: "ready",
    winner: null,
    eligibleSnapshot: [],
    startedAt: null,
    stopRequestedAt: null,
    stopReason: null,
    elapsedMs: 0,
  };
}

function safeFileBase(value: string): string {
  const normalized = value
    .normalize("NFKD")
    .replace(/[^\w\s-]/gu, "")
    .trim()
    .replace(/\s+/gu, "-")
    .toLocaleLowerCase();
  return normalized || "pickscape-list";
}

interface DrawContext {
  selectedCategories: string[];
  repeatMode: PickscapeProject["settings"]["repeatMode"];
  durationSeconds: number;
}

export default function App() {
  const [project, setProject] = useState<PickscapeProject>(() => initialProject());
  const [runtime, setRuntime] = useState<DrawRuntime>(() => initialRuntime());
  const [importSource, setImportSource] = useState<ImportSource>(null);
  const [rememberOnDevice, setRememberOnDevice] = useState(
    () => localStorage.getItem(REMEMBER_PREFERENCE_KEY) === "true",
  );
  const [hydrated, setHydrated] = useState(false);
  const backstageSidebar = useBackstageSidebar();
  const [notice, setNotice] = useState<{ tone: "success" | "error"; message: string } | null>(
    null,
  );
  const runtimeRef = useRef(runtime);
  const projectRef = useRef(project);
  const drawContextRef = useRef<DrawContext | null>(null);
  const initialRememberPreferenceRef = useRef(rememberOnDevice);

  useEffect(() => {
    runtimeRef.current = runtime;
  }, [runtime]);

  useEffect(() => {
    projectRef.current = project;
  }, [project]);

  useEffect(() => {
    if (!project.settings.soundEnabled) return;
    preloadLaunchMusic(
      project.settings.soundPack ?? "cinematic",
      project.settings.musicGenre ?? "follow-pack",
    );
  }, [
    project.settings.musicGenre,
    project.settings.soundEnabled,
    project.settings.soundPack,
  ]);

  useEffect(() => {
    let active = true;
    const restore = async () => {
      if (initialRememberPreferenceRef.current) {
        try {
          const remembered = await loadRememberedProject();
          if (active && remembered) setProject(remembered);
        } catch {
          if (active) {
            setNotice({ tone: "error", message: "Saved browser data could not be restored." });
          }
        }
      }
      if (active) setHydrated(true);
    };
    void restore();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!hydrated || !rememberOnDevice) return;
    const timer = window.setTimeout(() => {
      void saveRememberedProject(project).catch(() =>
        setNotice({ tone: "error", message: "The browser could not save this list." }),
      );
    }, 250);
    return () => window.clearTimeout(timer);
  }, [hydrated, project, rememberOnDevice]);

  useEffect(() => {
    const root = document.documentElement;
    root.dataset.appearance = project.settings.appearance;
  }, [project.settings.appearance]);

  const eligibleEntries = useMemo(() => getEligibleEntries(project), [project]);

  const changeProject = useCallback(
    (updater: (current: PickscapeProject) => PickscapeProject) => {
      setProject((current) => updater(current));
    },
    [],
  );

  const beginDraw = useCallback(
    (allowFromRevealed = false) => {
      const currentRuntime = runtimeRef.current;
      if (
        currentRuntime.status !== "ready" &&
        !(allowFromRevealed && currentRuntime.status === "revealed")
      ) {
        return;
      }

      const currentProject = projectRef.current;
      const selected = new Set(currentProject.settings.selectedCategories);
      const filtered = currentProject.entries.filter(
        (entry) =>
          entry.enabled &&
          (selected.size === 0 || (entry.category !== null && selected.has(entry.category))),
      );
      const eligible = getEligibleEntries(currentProject);

      if (eligible.length === 0) {
        setNotice({
          tone: "error",
          message:
            filtered.length > 0
              ? "No entries remain in no-repeat mode. Clear the results to reset the pool."
              : "Add or enable at least one eligible entry before drawing.",
        });
        return;
      }

      const winner = selectWeightedEntry(eligible);
      const startedAt = performance.now();
      drawContextRef.current = {
        selectedCategories: [...currentProject.settings.selectedCategories],
        repeatMode: currentProject.settings.repeatMode,
        durationSeconds: currentProject.settings.durationSeconds,
      };
      setRuntime({
        status: "running",
        winner,
        eligibleSnapshot: eligible,
        startedAt,
        stopRequestedAt: null,
        stopReason: null,
        elapsedMs: 0,
      });
      if (currentProject.settings.soundEnabled) {
        playLaunchCue(
          currentProject.settings.soundPack ?? "cinematic",
          currentProject.settings.musicGenre ?? "follow-pack",
          currentProject.settings.musicVolume ?? 58,
        );
      }
    },
    [],
  );

  const stopDraw = useCallback((reason: "manual" | "automatic") => {
    const current = runtimeRef.current;
    const currentProject = projectRef.current;
    const drawContext = drawContextRef.current;
    if (
      current.status !== "running" ||
      !current.winner ||
      current.startedAt === null ||
      !drawContext
    ) {
      return;
    }

    const stoppedAt = performance.now();
    const stopReason: DrawStopReason =
      reason === "manual"
        ? "manual"
        : drawContext.durationSeconds === 30
          ? "auto-30s"
          : "auto-duration";
    const elapsedMs = Math.max(0, stoppedAt - current.startedAt);
    const record: DrawRecord = {
      id: crypto.randomUUID(),
      drawNumber: currentProject.history.length + 1,
      timestamp: new Date().toISOString(),
      winnerId: current.winner.id,
      winnerName: current.winner.name,
      winnerCategory: current.winner.category,
      winnerWeight: current.winner.weight,
      eligibleCount: current.eligibleSnapshot.length,
      categoryFilter: drawContext.selectedCategories,
      repeatMode: drawContext.repeatMode,
      keptInPool: false,
      stopReason,
      durationMs: Math.round(elapsedMs),
    };

    setProject((projectValue) => ({
      ...projectValue,
      history: [...projectValue.history, record],
      updatedAt: new Date().toISOString(),
    }));
    setRuntime((runtimeValue) => ({
      ...runtimeValue,
      status: "stopping",
      stopRequestedAt: stoppedAt,
      stopReason,
      elapsedMs,
    }));
    if (currentProject.settings.soundEnabled) {
      playStopCue(currentProject.settings.soundPack ?? "cinematic");
    }
  }, []);

  useEffect(() => {
    if (runtime.status !== "running" || runtime.startedAt === null) return;
    const interval = window.setInterval(() => {
      setRuntime((current) => {
        if (current.status !== "running" || current.startedAt === null) return current;
        return { ...current, elapsedMs: performance.now() - current.startedAt };
      });
    }, 100);
    const timeout = window.setTimeout(
      () => stopDraw("automatic"),
      project.settings.durationSeconds * 1_000,
    );
    return () => {
      window.clearInterval(interval);
      window.clearTimeout(timeout);
    };
  }, [project.settings.durationSeconds, runtime.startedAt, runtime.status, stopDraw]);

  useEffect(() => {
    if (runtime.status !== "stopping") return;
    const delay = project.settings.motionReduced ? 180 : 1_100;
    const timer = window.setTimeout(() => {
      setRuntime((current) =>
        current.status === "stopping" ? { ...current, status: "revealed" } : current,
      );
      const currentProject = projectRef.current;
      if (currentProject.settings.soundEnabled && runtimeRef.current.winner) {
        playRevealCue(
          currentProject.settings.soundPack ?? "cinematic",
          currentProject.settings.celebrationVoice ?? "auto",
        );
      }
    }, delay);
    return () => window.clearTimeout(timer);
  }, [project.settings.motionReduced, runtime.status]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const isInteractive =
        target?.matches(
          "button, a[href], input, textarea, select, [contenteditable='true'], [role='button']",
        ) ?? false;
      if (isInteractive || importSource) return;

      if (event.key === "Escape") {
        if (runtimeRef.current.status === "revealed") setRuntime(initialRuntime());
        return;
      }
      if (event.key !== " " && event.key !== "Enter") return;
      event.preventDefault();
      if (runtimeRef.current.status === "running") stopDraw("manual");
      else if (runtimeRef.current.status === "ready") beginDraw();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [beginDraw, importSource, stopDraw]);

  useEffect(() => {
    if (!notice) return;
    const timer = window.setTimeout(() => setNotice(null), 4_000);
    return () => window.clearTimeout(timer);
  }, [notice]);

  const commitImport = (
    entries: PickscapeProject["entries"],
    mode: "replace" | "append",
  ) => {
    setProject((current) => ({
      ...current,
      entries: mergeEntries(current.entries, entries, mode),
      history: mode === "replace" ? [] : current.history,
      settings:
        mode === "replace"
          ? { ...current.settings, selectedCategories: [] }
          : current.settings,
      updatedAt: new Date().toISOString(),
    }));
    setImportSource(null);
    setNotice({
      tone: "success",
      message: `${entries.length.toLocaleString()} entries are ready for the stage.`,
    });
  };

  const restoreBackup = async (file: File) => {
    try {
      if (file.size > 5 * 1024 * 1024) throw new Error("Backups must be 5 MB or smaller.");
      const restored = parseProjectBackup(await file.text());
      setProject({ ...restored, updatedAt: new Date().toISOString() });
      setRuntime(initialRuntime());
      setNotice({ tone: "success", message: "Pickscape backup restored." });
    } catch (error) {
      setNotice({
        tone: "error",
        message: error instanceof Error ? error.message : "The backup could not be restored.",
      });
    }
  };

  const handleRememberChange = async (remember: boolean) => {
    setRememberOnDevice(remember);
    localStorage.setItem(REMEMBER_PREFERENCE_KEY, String(remember));
    if (remember) {
      await saveRememberedProject(projectRef.current);
      setNotice({ tone: "success", message: "This list will be remembered on this device." });
    } else {
      await clearRememberedProject();
      setNotice({ tone: "success", message: "Saved browser data was removed." });
    }
  };

  const deleteRememberedData = async () => {
    await clearRememberedProject();
    localStorage.setItem(REMEMBER_PREFERENCE_KEY, "false");
    setRememberOnDevice(false);
    setNotice({ tone: "success", message: "Saved browser data was removed." });
  };

  const closeResult = () => {
    setRuntime(initialRuntime());
    drawContextRef.current = null;
  };

  const keepWinnerInPool = () => {
    const winnerId = runtimeRef.current.winner?.id;
    if (winnerId) {
      setProject((current) => ({
        ...current,
        history: keepLatestWinnerInPool(current.history, winnerId),
        updatedAt: new Date().toISOString(),
      }));
    }
    closeResult();
  };

  const removeWinner = () => {
    const winnerId = runtimeRef.current.winner?.id;
    if (winnerId) {
      setProject((current) => ({
        ...current,
        entries: current.entries.map((entry) =>
          entry.id === winnerId ? { ...entry, enabled: false } : entry,
        ),
        updatedAt: new Date().toISOString(),
      }));
    }
    closeResult();
  };

  const toggleFullscreen = async () => {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        return;
      }
      const stage = document.getElementById("pickscape-stage");
      if (stage?.requestFullscreen) await stage.requestFullscreen();
      else document.documentElement.classList.toggle("presentation-fallback");
    } catch {
      setNotice({ tone: "error", message: "Presentation mode is unavailable in this browser." });
    }
  };

  const fileBase = safeFileBase(project.listName);
  const shellClassName = [
    "app-shell",
    !backstageSidebar.isMobile && backstageSidebar.collapsed && !backstageSidebar.pinned
      ? "sidebar-collapsed"
      : "",
    !backstageSidebar.isMobile && backstageSidebar.pinned ? "sidebar-pinned" : "",
    backstageSidebar.isMobile && backstageSidebar.mobileOpen ? "mobile-open" : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <div className={shellClassName}>
      <button
        type="button"
        className="backstage-backdrop"
        onClick={() => backstageSidebar.closeMobile(true)}
        aria-label="Close backstage"
        tabIndex={-1}
      />

      <BackstagePanel
        project={project}
        rememberOnDevice={rememberOnDevice}
        collapsed={backstageSidebar.collapsed}
        pinned={backstageSidebar.pinned}
        isMobile={backstageSidebar.isMobile}
        openOnMobile={backstageSidebar.mobileOpen}
        sidebarRef={backstageSidebar.sidebarRef}
        edgeToggleRef={backstageSidebar.edgeToggleRef}
        mobileCloseRef={backstageSidebar.mobileCloseRef}
        onPointerEnter={backstageSidebar.handlePointerEnter}
        onPointerLeave={backstageSidebar.handlePointerLeave}
        onFocusCapture={backstageSidebar.handleFocusCapture}
        onBlurCapture={backstageSidebar.handleBlurCapture}
        onRestoreDesktop={backstageSidebar.restoreDesktop}
        onTogglePinned={backstageSidebar.togglePinned}
        onCloseMobile={() => backstageSidebar.closeMobile(true)}
        onChangeProject={changeProject}
        onReviewImport={setImportSource}
        onRestoreBackup={(file) => void restoreBackup(file)}
        onRememberChange={(remember) => void handleRememberChange(remember)}
        onDeleteRememberedData={() => void deleteRememberedData()}
        onExportList={() =>
          downloadText(entriesToCsv(project.entries), `${fileBase}.csv`, "text/csv;charset=utf-8")
        }
        onExportHistory={() =>
          downloadText(
            historyToCsv(project.history),
            `${fileBase}-results.csv`,
            "text/csv;charset=utf-8",
          )
        }
        onExportBackup={() =>
          downloadText(
            projectToJson(project),
            `${fileBase}.pickscape.json`,
            "application/json;charset=utf-8",
          )
        }
        onUndoHistory={() =>
          setProject((current) => ({
            ...current,
            history: current.history.slice(0, -1),
            updatedAt: new Date().toISOString(),
          }))
        }
        onClearHistory={() =>
          setProject((current) => ({
            ...current,
            history: [],
            updatedAt: new Date().toISOString(),
          }))
        }
      />

      <StageShell
        entries={eligibleEntries}
        totalEntries={project.entries.length}
        runtime={runtime}
        settings={project.settings}
        backstageExpanded={
          backstageSidebar.isMobile
            ? backstageSidebar.mobileOpen
            : !backstageSidebar.collapsed || backstageSidebar.pinned
        }
        onOpenBackstage={backstageSidebar.openBackstage}
        onStart={() => beginDraw()}
        onStop={() => stopDraw("manual")}
        onThemeChange={(theme: ThemeId) =>
          setProject((current) => ({
            ...current,
            settings: { ...current.settings, theme },
            updatedAt: new Date().toISOString(),
          }))
        }
        onDurationChange={(durationSeconds) =>
          setProject((current) => ({
            ...current,
            settings: { ...current.settings, durationSeconds },
            updatedAt: new Date().toISOString(),
          }))
        }
        onSoundToggle={() =>
          setProject((current) => ({
            ...current,
            settings: {
              ...current.settings,
              soundEnabled: !current.settings.soundEnabled,
            },
            updatedAt: new Date().toISOString(),
          }))
        }
        onFullscreen={() => void toggleFullscreen()}
        onCloseResult={closeResult}
        onKeepWinner={keepWinnerInPool}
        onRemoveWinner={removeWinner}
        onDrawAgain={() => beginDraw(true)}
      />

      <ImportDialog
        source={importSource}
        existingEntries={project.entries}
        onClose={() => setImportSource(null)}
        onCommit={commitImport}
      />

      <AnimatePresence>
        {notice ? (
          <motion.div
            className={`notice ${notice.tone}`}
            initial={
              project.settings.motionReduced ? false : { opacity: 0, y: 18, scale: 0.98 }
            }
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={
              project.settings.motionReduced
                ? { opacity: 1, y: 0, scale: 1 }
                : { opacity: 0, y: 12 }
            }
            transition={project.settings.motionReduced ? { duration: 0 } : undefined}
            role={notice.tone === "error" ? "alert" : "status"}
          >
            {notice.tone === "success" ? (
              <CheckCircle size={20} weight="fill" />
            ) : (
              <WarningCircle size={20} weight="fill" />
            )}
            <span>{notice.message}</span>
            <button type="button" onClick={() => setNotice(null)} aria-label="Dismiss message">
              <X size={16} />
            </button>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
}
